import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddRateItem } from '../../hooks/useAddRateItem';
import { useActiveOwnBoxes } from '../../hooks/useActiveOwnBoxes';
import { RateItem } from '../../models/rate.types';
import {
  buildPackagingRateItem,
  getCurrentPackagingRateItem,
  mergePackagingPrices,
} from '../../utils/packagingRateItem';
import { translateRateError } from '../../utils/rateErrorTranslator';
import { PackagingPricesTable } from '../interaction/PackagingPricesTable';

export interface EditPackagingPricesModalRef {
  open: () => void;
}

interface EditPackagingPricesModalProps {
  rateId: number;
  items: RateItem[];
  onSuccess?: () => void;
}

export const EditPackagingPricesModal = forwardRef<EditPackagingPricesModalRef, EditPackagingPricesModalProps>(
  ({ rateId, items, onSuccess }, ref) => {
    const { t } = useTranslation('ratesModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const { ownBoxes, isLoading: isLoadingBoxes } = useActiveOwnBoxes();
    const { mutateAsync: addItem, isPending } = useAddRateItem();

    const [prices, setPrices] = useState<Record<number, number>>({});
    const [priceErrors, setPriceErrors] = useState<Record<number, string>>({});

    const initializePrices = useCallback(() => {
      const currentItem = getCurrentPackagingRateItem(items);
      setPrices(mergePackagingPrices(ownBoxes, currentItem));
      setPriceErrors({});
    }, [items, ownBoxes]);

    useImperativeHandle(ref, () => ({
      open: () => {
        initializePrices();
        modalRef.current?.openModal();
      },
    }));

    useEffect(() => {
      if (ownBoxes.length > 0) {
        initializePrices();
      }
    }, [ownBoxes, initializePrices]);

    const handlePriceChange = (packagingId: number, value: string) => {
      const parsed = value.trim() === '' ? NaN : parseFloat(value);
      setPrices((prev) => ({
        ...prev,
        [packagingId]: Number.isNaN(parsed) ? 0 : parsed,
      }));
      setPriceErrors((prev) => {
        const next = { ...prev };
        delete next[packagingId];
        return next;
      });
    };

    const validatePrices = (): boolean => {
      const errors: Record<number, string> = {};
      for (const box of ownBoxes) {
        const price = prices[box.id];
        if (price === undefined || price < 0 || Number.isNaN(price)) {
          errors[box.id] = t('validation.feePositive');
        }
      }
      setPriceErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
      if (!validatePrices()) {
        toast({
          title: t('validation.incompleteForm'),
          variant: 'destructive',
        });
        return;
      }

      try {
        const payload = buildPackagingRateItem(ownBoxes, prices);
        await addItem({ rateId, data: payload });

        toast({
          title: t('toast.packagingPricesUpdated'),
        });

        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('toast.addItemError'),
          description: translateRateError(error, t),
          variant: 'destructive',
        });
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modal.editPackagingPrices.title')}
        type="default"
        width="lg"
        classNameBody="p-0 flex flex-col max-h-[calc(100vh-200px)]"
        data-testid="modal-edit-packaging-prices"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <p className="text-sm text-subtext">{t('modal.editPackagingPrices.description')}</p>
            {isLoadingBoxes ? (
              <p className="text-sm text-subtext">{t('sections.packagingPrices.loading')}</p>
            ) : (
              <PackagingPricesTable
                ownBoxes={ownBoxes}
                prices={prices}
                onPriceChange={handlePriceChange}
                errors={priceErrors}
              />
            )}
          </div>

          <div className="flex justify-end gap-3 p-4 border-t border-card-border bg-body-bg">
            <Button type="button" variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isPending}>
              {t('modal.editPackagingPrices.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              className="!text-white"
              onClick={handleSubmit}
              disabled={isPending || isLoadingBoxes || ownBoxes.length === 0}
              data-testid="save-packaging-prices-button"
            >
              {isPending ? t('modal.editPackagingPrices.saving') : t('modal.editPackagingPrices.submit')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

EditPackagingPricesModal.displayName = 'EditPackagingPricesModal';
